import type { Container } from '../db/board';
import type { Item, Section } from '../db/schema';
import { AddItemDialog } from './AddItemDialog';
import { DeleteItemDialog } from './DeleteItemDialog';
import { DeleteItemsDialog } from './DeleteItemsDialog';
import { MoveItemsDialog } from './MoveItemsDialog';
import { SectionDialog } from './SectionDialog';
import { DeleteSectionDialog } from './DeleteSectionDialog';

/** What the board can have open over it. One place, so `Board` reads. */
export type BoardDialog =
  | { kind: 'add-item' }
  | { kind: 'add-section' }
  | { kind: 'edit-section'; section: Section }
  | { kind: 'delete-section'; section: Section }
  | { kind: 'delete-item'; item: Item }
  /** One item from its own menu, or a whole selection from the bar. */
  | { kind: 'move'; itemIds: readonly string[] }
  | { kind: 'delete-items'; itemIds: readonly string[] };

export function BoardDialogs({
  open,
  container,
  parentNameOf,
  onClose,
  onItemDeleted,
  onItemsDeleted,
  onItemsMoved,
  onSectionDeleted,
  onItemImported,
}: {
  open: BoardDialog | undefined;
  container: Container;
  /** Where a deleted section's contents will land, for the confirmation. */
  parentNameOf: (section: Section) => string;
  onClose: () => void;
  onItemDeleted: (item: Item) => void;
  onItemsDeleted: () => void;
  onItemsMoved: (roomId: string) => void;
  onSectionDeleted: (section: Section) => void;
  /** Opens the panel, so the import's progress has somewhere to show. */
  onItemImported: (itemId: string) => void;
}) {
  if (open === undefined) return null;

  switch (open.kind) {
    case 'add-item':
      return (
        <AddItemDialog
          container={container}
          onClose={onClose}
          onAdded={onClose}
          onImported={onItemImported}
        />
      );

    case 'add-section':
      return <SectionDialog container={container} onClose={onClose} />;

    case 'edit-section':
      return <SectionDialog container={container} section={open.section} onClose={onClose} />;

    case 'delete-section':
      return (
        <DeleteSectionDialog
          section={open.section}
          parentName={parentNameOf(open.section)}
          onClose={onClose}
          onDeleted={() => onSectionDeleted(open.section)}
        />
      );

    case 'delete-item':
      return (
        <DeleteItemDialog
          item={open.item}
          onClose={onClose}
          onDeleted={onItemDeleted}
        />
      );

    case 'delete-items':
      return (
        <DeleteItemsDialog
          itemIds={open.itemIds}
          onClose={onClose}
          onDeleted={onItemsDeleted}
        />
      );

    case 'move':
      return (
        <MoveItemsDialog
          itemIds={open.itemIds}
          fromRoomId={container.roomId}
          onClose={onClose}
          onMoved={onItemsMoved}
        />
      );
  }
}
